import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ChevronLeft, Share2, ArrowRight } from "lucide-react";

import MethodsListTab from "@/pages/coach/methods/MethodsListTab";
import TemplatesTab from "@/pages/coach/methods/TemplatesTab";
import StatsTab from "@/pages/coach/methods/StatsTab";
import BreathworkTab from "@/pages/coach/methods/BreathworkTab";
import { MethodEditorDialog } from "@/pages/coach/methods/MethodEditorDialog";
import { MethodPreviewDialog } from "@/pages/coach/methods/MethodPreviewDialog";

export default function Methods() {
  const { user } = useAuth();
  const t = useT();
  usePageMeta(t("cm.meta_title"));
  const [editor, setEditor] = useState(null); // { methodId } | { new: true }
  const [preview, setPreview] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const bump = () => setRefreshToken((n) => n + 1);
  const openNew = () => setEditor({ new: true });
  const openEdit = (methodId) => setEditor({ methodId });
  const closeEditor = (saved) => { setEditor(null); if (saved) bump(); };

  return (
    <div data-testid="coach-methods-page">
      <Seo title={t("cm.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="methods-back">
        <ChevronLeft className="h-4 w-4" /> {t("cm.back")}
      </Link>
      <PageHeader eyebrow={t("cm.eyebrow")} title={t("cm.title")} subtitle={t("cm.subtitle")} />

      <Tabs defaultValue="list" className="mt-6">
        <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1 bg-secondary/60 p-1">
          <TabsTrigger value="list" data-testid="cm-tab-list">{t("cm.tab_methods")}</TabsTrigger>
          <TabsTrigger value="templates" data-testid="cm-tab-templates">{t("cm.tab_templates")}</TabsTrigger>
          <TabsTrigger value="breathwork" data-testid="cm-tab-breathwork">{t("cm.tab_breathwork")}</TabsTrigger>
          <TabsTrigger value="stats" data-testid="cm-tab-stats">{t("cm.tab_stats")}</TabsTrigger>
          <TabsTrigger value="social" data-testid="cm-tab-social">{t("cm.tab_social")}</TabsTrigger>
        </TabsList>
        <TabsContent value="list"><MethodsListTab refreshToken={refreshToken} onNew={openNew} onEdit={openEdit} onPreview={setPreview} onChanged={bump} /></TabsContent>
        <TabsContent value="templates"><TemplatesTab refreshToken={refreshToken} onEdit={openEdit} onChanged={bump} /></TabsContent>
        <TabsContent value="breathwork"><BreathworkTab /></TabsContent>
        <TabsContent value="stats"><StatsTab onEdit={openEdit} /></TabsContent>
        <TabsContent value="social"><SocialTab t={t} /></TabsContent>
      </Tabs>

      {editor && <MethodEditorDialog open methodId={editor.methodId || null} initialKind={null} onClose={closeEditor} />}
      {preview && <MethodPreviewDialog method={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function SocialTab({ t }) {
  return (
    <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center" data-testid="cm-social-tab">
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10"><Share2 className="h-6 w-6 text-primary" /></div>
      <p className="mt-4 font-display text-lg text-foreground">{t("cm.social_title")}</p>
      <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">{t("cm.social_sub")}</p>
      <Link to="/app/coach/content" data-testid="cm-social-cta"
        className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90">
        {t("cm.social_cta")} <ArrowRight className="h-4 w-4" />
      </Link>
    </div>
  );
}
